import { z } from "zod";

import {
  emptyToUndefined,
  normalizeEnvString,
  optionalSecret,
  optionalUrl,
  requiredUrl,
} from "./_shared";

/** "true"/"false" kill switch that stays dormant when the row is absent or blank. */
const optionalBooleanString = z.preprocess(
  emptyToUndefined,
  z.enum(["true", "false"]).optional(),
);

const publicSchema = z.object({
  NEXT_PUBLIC_SUPABASE_URL: requiredUrl,
  NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: z.preprocess(
    normalizeEnvString,
    z.string(),
  ),
  NEXT_PUBLIC_SITE_URL: requiredUrl,
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string().min(1).optional(),
  NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID: z.string().min(1).optional(),
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: z.string().min(1).optional(),
  NEXT_PUBLIC_FACEBOOK_GATEWAY_URL: optionalUrl,
  // Origin of services/sse-bridge, e.g. https://api.atriam.ai. The realtime
  // ticket route hands this to the browser alongside the ticket, so the client
  // never reads an env var itself. Absent → the route 503s and
  // `useTableEvents` stays dormant, which is the dormant state this ships in.
  NEXT_PUBLIC_REALTIME_BRIDGE_URL: optionalUrl,
  // NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT and NEXT_PUBLIC_SIGNOZ_INGEST_TOKEN
  // were removed when browser OTel moved to a same-origin proxy at
  // /api/otlp/*. The proxy reads the (server-only) OTEL_EXPORTER_OTLP_ENDPOINT
  // + SIGNOZ_INGEST_TOKEN and forwards.
  NEXT_PUBLIC_POSTHOG_KEY: optionalSecret,
  NEXT_PUBLIC_APP_VERSION: optionalSecret,
  NEXT_PUBLIC_SUPABASE_TEST_MODE: optionalBooleanString,
});

export const publicEnv = publicSchema.parse({
  NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
  NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY:
    process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY,
  NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
  NEXT_PUBLIC_FACEBOOK_GATEWAY_URL:
    process.env.NEXT_PUBLIC_FACEBOOK_GATEWAY_URL,
  NEXT_PUBLIC_REALTIME_BRIDGE_URL: process.env.NEXT_PUBLIC_REALTIME_BRIDGE_URL,
  NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
  NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
  NEXT_PUBLIC_SUPABASE_TEST_MODE: process.env.NEXT_PUBLIC_SUPABASE_TEST_MODE,
});

export type PublicEnv = typeof publicEnv;
